import React, { useState, useEffect } from 'react';
import useRetrieveData from 'global/hooks/useRetrieveData';
import { get, shuffle, take } from 'global/lib/lodash';
import { StyledQuestionsContainer } from './styles';
import { Text } from 'global/components/text';
import { Column, Row } from 'global/components/row';
import { Button } from 'global/components/Button';
import { ImageCarousel } from 'global/components/image';
import { Checkbox, Icon, Tooltip } from '@mui/material';
import SnackAlerts from 'global/components/snack-alerts';
import { BorderLinearProgress } from '../courses';
import { scrollTop, _size } from 'global/utils';
import clsx from 'clsx';
import sendData from 'redux/actions/sendData';
import ScoreIcon from 'global/components/ScoreIcon';
import { withRouter } from 'react-router-dom';
import { Loader, useScreen } from 'global/styles';
import NavToTop from 'feature/nav/NavToTop';
import { awaitAll } from 'global/utils/awaitAll';
import { COURSE_DOCUMENT_SCROLL_ID } from '../courseShared';

export const Quiz = withRouter((props) => {
  const { isMobile } = useScreen();

  const { documents, document, updateReadStatus, footer } = props;

  const checklistid = get(document, 'documentableId');

  const { isReview } = get(props, 'location.state', {});

  const course_assignment_id = get(props, 'documents.id');
  const scorecard_id = get(props, 'documents.scorecardId');

  const takeAmt = parseInt(get(document, 'meta.questionCount', 10));
  const passingVal = parseInt(get(document, 'meta.passingScore', 80));
  const randomOptions = get(document, 'meta.randomOptions', true);
  const randomCheckpoints = get(document, 'meta.randomCheckpoints', true);

  const [questions, setQuestions] = useState([]);
  const [state, setState] = useState({
    checklistid,
    currentQuestion: -1,
    answers: {},
    showResults: false,
    scoreValue: get(documents, 'score', 0),
  });
  const [alerts, setAlerts] = useState();

  const { data, update, loading } = useRetrieveData(
    { url: `/checklists/${checklistid}` },
    {
      useEffect: true,
      waitFor: !!checklistid,
      build: ['checklists'],
      params: {
        filter: {
          course_assignment_id,
          scorecard_id,
        },
        include: ['checkpoints', 'checkpoints.checkpointOptions', 'checkpoints.images', 'scorecards', 'scorecardOptions', 'scorecardScores'].join(','),
      },
      transformData: (d) => get(d, 0),
    }
  );

  const changeQuestion = (dir = 1) => {
    setState((s) => ({ ...s, currentQuestion: s.currentQuestion + dir }));
  };

  const setAnswer = (q) => {
    setState((s) => ({
      ...s,
      answers: { ...s.answers, [s.currentQuestion]: q },
      currentQuestion: s.currentQuestion + 1,
    }));
  };

  const hasScores = !!_size(get(data, 'scorecardScores', []));

  const deleteScores = async () => {
    if (hasScores) {
      await Promise.all(get(data, 'scorecardScores', []).map((sc) => sendData({ url: `/scorecard-scores/${sc.id}`, method: 'delete' })));
    }
  };

  const submit = async () => {
    try {
      setState((s) => ({ ...s, loading: true }));

      await deleteScores();

      const res = questions
        .map((q, i) => {
          if (!q.title) {
            const answer = state.answers[i];
            const cpo = get(data, 'scorecardOptions', []).find((sc) => sc.checkpointOptionId == answer.id);

            return {
              data: {
                type: 'scorecard-scores',
                attributes: {
                  course_assignment_id,
                  scorecard_id,
                  checklist_id: `${checklistid}`,
                  checkpoint_id: `${get(cpo, 'checkpointId')}`,
                  checkpoint_option_id: `${get(cpo, 'checkpointOptionId')}`,
                  scorecard_option_id: `${get(cpo, 'id')}`,
                  score: `${get(cpo, 'value')}`,
                },
              },
            };
          }
        })
        .filter((f) => !!f);

      await awaitAll(
        res.map((r) => () => async () => {
          await sendData({
            url: `/scorecard-scores`,
            method: 'post',
            data: r,
          });
        }),
        { queueSize: 1 }
      );

      await Promise.all([props.update && props.update(), update()]);
      setState((s) => ({ ...s, showResults: true, currentQuestion: 0 }));
    } catch (err) {
      console.error(err);
      setAlerts({ message: err.message, severity: 'error' });
    } finally {
      setState((s) => ({ ...s, loading: false }));
    }
  };

  const getLastSlide = () => {
    const unanswered = questions.filter((q, i) => !state.answers[i] && !q.title);

    if (_size(unanswered)) {
      return {
        className: 'intro',
        title: 'Not Quite Done!',
        description: `Thats the end of the quiz; but you've skipped something. Use the "Prev" button to go back and finish.`,
        buttons: [{ title: 'SUBMIT', disabled: true }],
      };
    }

    return {
      className: 'intro',
      title: `That's it!`,
      description: `That's the end of the quiz; if you're satisfied with your answers submit to get your results.` + (hasScores ? '<br /><br /><b>This will overwrite your previous scores.</b>' : ''),
      buttons: [{ title: 'SUBMIT', onClick: submit, loading: state.loading }],
    };
  };

  const getFirstSlide = () => {
    if (state.showResults) {
      return {
        className: 'intro',
        title: 'Quiz Stuff',
        description: `During this quiz you will be presented with ${_size(questions)} multiple choice questions.
<br />
<br />
You must <b>finish in one sitting</b>. If you navigate away from the quiz or come back later, your answers won't be saved. You must also <b>answer all the questions</b> to submit, but feel free to skip around using the Next and Previous buttons up until that point.
<br />
<br />
Click "Next" to get started!`,
      };
    } else {
      return {
        className: 'intro',
        title: 'Quiz',
        description: `During this quiz you will be presented with ${_size(questions)} multiple choice questions.
<br />
<br />
You must <b>finish in one sitting</b>. If you navigate away from the quiz or come back later, your answers won't be saved. You must also <b>answer all the questions</b> to submit, but feel free to skip around using the Next and Previous buttons up until that point.
<br />
<br />
Click "Next" to get started!`,
      };
    }
  };

  const generateQuestions = (reset = false) => {
    const cps = randomCheckpoints ? shuffle(data.checkpoints) : data.checkpoints;
    setQuestions(take(cps, takeAmt).map((q) => ({ ...q, checkpointOptions: randomOptions && !isTF(q.checkpointOptions) ? shuffle(q.checkpointOptions) : q.checkpointOptions })));
    setState((s) => ({
      ...s,
      currentQuestion: -1,
      answers: reset ? {} : s.answers,
      showResults: false,
      checklistid,
    }));
  };

  const isCurrentlyTakingQuiz = !!_size(state.answers) && state.currentQuestion > -1 && !state.showResults;

  useEffect(() => {
    const unblock = props.history.block((nextLocation) => {
      if (isCurrentlyTakingQuiz) {
        const canLeave = window.confirm(`Are you sure you want to leave? Your currently selected answers will be lost.`);
        return canLeave;
      }
    });

    return () => {
      unblock();
    };
  }, [isCurrentlyTakingQuiz]);

  useEffect(() => {
    if (data && !loading) {
      if ((state.checklistid !== checklistid && get(data, 'id') == checklistid) || !_size(questions)) {
        generateQuestions(state.checklistid !== checklistid);
      }
    }
  }, [data, checklistid, loading]);

  useEffect(() => {
    if (hasScores) {
      const cps = get(data, 'checkpoints', []);
      const scores = get(data, 'scorecardScores', []);
      const totalCorrect = _size(scores.filter((s) => parseInt(get(s, 'score')) === 100));
      const scoreValue = get(documents, 'score', Math.round((totalCorrect / takeAmt) * 100));

      const answers = {};
      const ques = scores.map((sc, i) => {
        const cp = cps.find((cp) => cp.id == sc.checkpointId);
        const answer = get(cp, 'checkpointOptions', []).find((cpo) => cpo.id == sc.checkpointOptionId);
        if (answer) {
          answers[i] = answer;
        }
        return cp;
      });
      setState((s) => ({
        ...s,
        scoreValue,
        answers,
        showResults: true,
      }));
      setQuestions(ques);

      if (scoreValue >= passingVal) {
        updateReadStatus(true);
      }
    }
  }, [data]);

  const renderAnswer = (q, i) => {
    const answer = get(state, ['answers', i]);
    const answeredCorrect = get(answer, 'value') === 100;
    return (
      <div className="answerContainer">
        <Text type="title_small">
          <Text bold>{answeredCorrect ? 'Correct!' : 'Incorrect: '}</Text>
          {!answeredCorrect && get(q, 'notes', 'N/A')}
        </Text>
      </div>
    );
  };

  // console.log({
  //   data,
  //   props,
  //   questions,
  //   state,
  // })

  const prog = ((state.currentQuestion + 1) / (_size(questions) + 1)) * 100;

  if (state.showResults) {
    const passed = state.scoreValue >= passingVal;
    return (
      <StyledQuestionsContainer isMobile={isMobile} className="resultsContainer">
        <NavToTop onClick={() => scrollTop(`#${COURSE_DOCUMENT_SCROLL_ID}`)} />
        <div className={'questionContainer'}>
          <Row>
            <Text style={{ marginRight: 16 }} type="title_large" bold>
              Results
            </Text>
            <ScoreIcon score={state.scoreValue} />
            <Text type="title_large" bold>{`${state.scoreValue}%`}</Text>
          </Row>
          {!passed && (
            <Text
              type="title_medium"
              bold
            >{`Your score did not meet the minimum of ${passingVal}% needed to pass this  quiz. Please review your results and retake the quiz to continue with the course.`}</Text>
          )}
          {!isReview && (
            <Tooltip title={'Retaking a quiz will overwrite your current scores, but not until you submit it.'}>
              <span>
                <Button onClick={() => generateQuestions(true)} style={{ marginTop: 8 }}>
                  RETAKE QUIZ
                </Button>
              </span>
            </Tooltip>
          )}
        </div>
        {questions.map((q, i) => {
          const Comp = isMobile ? Column : Row;
          return (
            <Comp>
              <div className={'questionContainer'}>
                <Question question={q} currentQuestion={i} showAnswer answer={get(state, ['answers', i])} />
              </div>
              {renderAnswer(q, i)}
            </Comp>
          );
        })}
        {footer}
      </StyledQuestionsContainer>
    );
  }

  if (isReview && !loading) {
    return (
      <StyledQuestionsContainer isMobile={isMobile}>
        <div className="scrollContainer">
          <Question
            currentQuestion={0}
            answer={get(state.answers, state.currentQuestion, {})}
            question={{
              className: 'intro',
              title: 'Quiz',
              description: `This user has not yet taken this quiz.`,
            }}
            setAnswer={setAnswer}
          />
          {footer}
        </div>
      </StyledQuestionsContainer>
    );
  }

  return loading ? (
    <StyledQuestionsContainer isMobile={isMobile}>
      <Column>
        <Loader />
      </Column>
    </StyledQuestionsContainer>
  ) : (
    <StyledQuestionsContainer isMobile={isMobile}>
      <div className="scrollContainer">
        <Question
          currentQuestion={state.currentQuestion}
          answer={get(state.answers, state.currentQuestion, {})}
          question={state.currentQuestion === -1 ? getFirstSlide() : state.currentQuestion >= questions.length ? getLastSlide() : get(questions, state.currentQuestion, {})}
          setAnswer={setAnswer}
        />
        {footer}
      </div>

      <Column center className="buttonContainer">
        <div className="prog">
          <BorderLinearProgress variant="determinate" value={prog} />
        </div>

        <Row className="buttonRow">
          <Tooltip title={'Previous'}>
            <span>
              <Button disabled={state.currentQuestion <= -1} onClick={() => changeQuestion(-1)} startIcon={<Icon>arrow_left</Icon>}>
                Prev
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={'Next'}>
            <span>
              <Button disabled={state.currentQuestion === questions.length} onClick={() => changeQuestion(1)} startIcon={<Icon>arrow_right</Icon>}>
                Next
              </Button>
            </span>
          </Tooltip>
        </Row>
      </Column>
      <SnackAlerts alerts={alerts} setAlerts={setAlerts} />
    </StyledQuestionsContainer>
  );
});

const isTF = (cpos) => {
  return _size(cpos) === 2 && !!cpos.find((cpo) => cpo.name === 'True');
};

const Question = (props) => {
  const { question, currentQuestion, answer, setAnswer, showAnswer } = props;

  const { checkpointOptions } = question;

  const images = get(question, 'images', []);
  const hasImages = !!_size(images);
  const isBoolQ = isTF(checkpointOptions);

  const title = (() => {
    if (question.title) {
      return typeof question.title === 'function' ? question.title() : question.title;
    } else {
      return `Question ${currentQuestion + 1}`;
    }
  })();

  const __html = (() => {
    const d = get(question, 'description', '');
    return typeof d === 'function' ? d() : d;
  })();

  const isCorrect = (cpo) => {
    return get(cpo, 'value', 0) === 100;
  };

  const renderIcon = (cpo) => {
    const correct = isCorrect(cpo);
    if (showAnswer) {
      return <Icon>{correct ? 'check_circle' : 'cancel'}</Icon>;
    } else {
      return <Checkbox checked={get(answer, 'id') === cpo.id} />;
    }
  };

  const buttons = get(question, 'buttons', []);

  return (
    <div className={clsx('question', get(question, 'className', ''), showAnswer ? 'showAnswer' : '')}>
      <Text className="header" type="title_large">
        {title}
      </Text>
      <Text className="description" type="title_small" dangerouslySetInnerHTML={{ __html }} />

      {hasImages && <ImageCarousel key={get(question, 'id', 0)} images={images} />}

      {!!_size(buttons) && (
        <Row className="submitRow" center>
          {buttons.map((btn) => (
            <Button {...btn}>{btn.title}</Button>
          ))}
        </Row>
      )}

      <div className={clsx('options', isBoolQ && 'boolean')}>
        {(checkpointOptions || []).map((cpo) => {
          const correct = isCorrect(cpo);
          const isSelected = get(answer, 'id') == cpo.id;
          return (
            <Row className={clsx('checkpointOption', isSelected && 'selected', showAnswer && correct ? 'correct' : 'incorrect')} onClick={setAnswer ? () => setAnswer(cpo) : () => 0}>
              {renderIcon(cpo)}
              <Text type="title_small">{cpo.name}</Text>
            </Row>
          );
        })}
      </div>
    </div>
  );
};
